import {areSetsEqual} from '@benchling/js-utils';
import {groupByUniqueKey} from '@benchling/js-utils';
import isEqual from 'react-fast-compare';

import {ProcessFlowchartRouterNode} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartNodeDetailType';
import {isRouterNode} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForInteractiveNode';
import {
  RouterConfig,
  CreateOrUpdateRouterFunctionSpec,
} from 'processes/modals/router-config/RouterConfigConstants';
import {omit} from 'util/omitKey';

import {ProcessInteractiveFlowchart} from '../ProcessFlowchartVizConstants';
import {getClientIdForProcessFlowchartEdge} from './ConvertProcessFlowchartForReactFlowUtil';

function getNodeIdsSet(flowchart: ProcessInteractiveFlowchart): Set<string> {
  return new Set(flowchart.nodes.map(({clientId}) => clientId));
}

function getEdgesSet(flowchart: ProcessInteractiveFlowchart): Set<string> {
  return new Set(
    flowchart.edges.map(({fromNodeClientId, toNodeClientId}) =>
      getClientIdForProcessFlowchartEdge(fromNodeClientId, toNodeClientId)
    )
  );
}

type WithoutClientId<T extends {clientId: string}> = Omit<T, 'clientId'>;
type RouterConfigWithoutClientId = Omit<
  RouterConfig,
  'clientId' | 'functionSpecs' | 'defaultFunctionSpec'
> & {
  functionSpecs: Array<WithoutClientId<CreateOrUpdateRouterFunctionSpec>>;
  defaultFunctionSpec: WithoutClientId<CreateOrUpdateRouterFunctionSpec> | null;
};

function removeRouterConfigClientId(routerConfig: RouterConfig): RouterConfigWithoutClientId {
  const functionSpecsWithoutClientId: Array<Omit<CreateOrUpdateRouterFunctionSpec, 'clientId'>> =
    routerConfig.functionSpecs.map((functionSpec) => omit(functionSpec, 'clientId'));
  const defaultFunctionSpecWithoutClientId: Omit<CreateOrUpdateRouterFunctionSpec, 'clientId'> | null =
    routerConfig.defaultFunctionSpec != null ? omit(routerConfig.defaultFunctionSpec, 'clientId') : null;

  const {clientId, ...routerConfigWithoutClientId} = routerConfig;
  return {
    ...routerConfigWithoutClientId,
    defaultFunctionSpec: defaultFunctionSpecWithoutClientId,
    functionSpecs: functionSpecsWithoutClientId,
  };
}

function haveRouterNodesChanged(
  flowchartA: ProcessInteractiveFlowchart,
  flowchartB: ProcessInteractiveFlowchart
): boolean {
  const getRouterNodesById = (
    flowchart: ProcessInteractiveFlowchart
  ): {[id: string]: ProcessFlowchartRouterNode} =>
    groupByUniqueKey(
      flowchart.nodes.filter((node): node is ProcessFlowchartRouterNode => isRouterNode(node)),
      ({clientId}) => clientId
    );

  const routerNodesByIdA = getRouterNodesById(flowchartA);
  const routerNodesByIdB = getRouterNodesById(flowchartB);
  if (!areSetsEqual(new Set(Object.keys(routerNodesByIdA)), new Set(Object.keys(routerNodesByIdB)))) {
    return true;
  }
  for (const clientId of Object.keys(routerNodesByIdA)) {
    const routerConfigA = routerNodesByIdA[clientId].routerConfig;
    const routerConfigB = routerNodesByIdB[clientId].routerConfig;
    if (!isEqual(removeRouterConfigClientId(routerConfigA), removeRouterConfigClientId(routerConfigB))) {
      return true;
    }
  }
  return false;
}

export default function areProcessInteractiveFlowchartsDifferent(
  flowchartA: ProcessInteractiveFlowchart | null,
  flowchartB: ProcessInteractiveFlowchart | null
): boolean {
  if (flowchartA == null) {
    return flowchartB != null;
  } else if (flowchartB == null) {
    return true;
  } else if (!areSetsEqual(getNodeIdsSet(flowchartA), getNodeIdsSet(flowchartB))) {
    return true;
  } else if (!areSetsEqual(getEdgesSet(flowchartA), getEdgesSet(flowchartB))) {
    return true;
  } else if (haveRouterNodesChanged(flowchartA, flowchartB)) {
    return true;
  }
  return false;
}
